import axios from "axios";
import {
  getUrlForAllActivitiesForAllClientsByDate,
  getUrlForAllActivitiesForClientByDate,
  getUrlForAllActivitiesForMemberByDate,
} from "utils/endpointUtils/activitiesEndpointUtil";
import {
  getUrlForClientByMemberUserId,
  getUrlForClientByClientUserId,
  getUrlForAllClients,
  getUrlForClientsPage,
  getUrlToCreateClient,
  getUrlToUpdateClient,
  getUrlToDeleteClient,
  getUrlForAllClientMappings,
  getUrlForClientMappingsPage,
  getUrlToCreateClientMapping,
  getUrlToUpdateClientMapping,
  getUrlToDeleteClientMapping,
  getUrlForClientsForBdm,
  getUrlForLoadAllLocationsFromBackend,
  getUrlForLoadAllWorkingHoursByClientFromBackend,
  getUrlToCreateWorkingHour,
  getUrlToUpdateWorkingHour,
  getUrlToDeleteWorkingHour,
} from "utils/endpointUtils/clientEndpointUtil";
import { setAuthHeader } from "app/services/auth.header.service";
import { getUrlForAllLeavePlansForClientByDate } from "utils/endpointUtils/leavePlansEndpointUtil";

export const loadActivitiesFromBackendForClient = async (
  clientId,
  fromDate,
  toDate,
  selectedMember = 0,
  keyword = ""
) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url =
    selectedMember === 0
      ? getUrlForAllActivitiesForClientByDate(clientId, fromDate, toDate)
      : getUrlForAllActivitiesForMemberByDate(selectedMember, fromDate, toDate);

  return await axios.get(url, config);
};

export const loadAllLocationsFromBackend = async () => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForLoadAllLocationsFromBackend();

  return await axios.get(url, config);
};

export const loadAllWorkingHoursByClient = async (clientId) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForLoadAllWorkingHoursByClientFromBackend(clientId);

  return await axios.get(url, config);
};

export const addWorkingHourToBackend = async (data) => {
    const config = setAuthHeader();
    if (config === null) throw new Error("Invalid Auth Header or Token Expired");
  
    const url = getUrlToCreateWorkingHour();
    return await axios.post(url, data, config);
  };

  export const updateWorkingHourToBackend = async (id, data) => {
    const config = setAuthHeader();
    if (config === null) throw new Error("Invalid Auth Header or Token Expired");
  
    const url = getUrlToUpdateWorkingHour(id);
    return await axios.put(url, data, config);
  };

export const deleteWorkingHour = async (id) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlToDeleteWorkingHour(id);

  return await axios.delete(url, config);
};

export const loadActivitiesFromBackendForAllClients = async (
  fromDate,
  toDate,
  selectedMember = 0,
  keyword = ""
) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url =
    selectedMember === 0
      ? getUrlForAllActivitiesForAllClientsByDate(fromDate, toDate)
      : getUrlForAllActivitiesForMemberByDate(selectedMember, fromDate, toDate);

  return await axios.get(url, config);
};

/**
 * Returns promise by calling "GET v1/api/clients/byuser/{userId}" endpoint
 * @param {*} clientUserId for which clientId to be fetched from backend
 * @returns promise which returns member id for user
 */
export const loadClientByClientUserId = async (clientUserId) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlForClientByClientUserId(clientUserId);
  return await axios.get(url, config);
};

/**
 * Returns promise by calling "GET v1/api/clients/byMemberUser/{memberUserId}" endpoint
 * @param {*} userId for which clientId to be fetched from backend
 * @returns promise which returns member id for user
 */
export const loadClientByMemberUserId = async (memberUserId) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlForClientByMemberUserId(memberUserId);
  return await axios.get(url, config);
};

export const loadLeavePlansFromBackendForClient = async (clientId, fromDate, toDate) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForAllLeavePlansForClientByDate(clientId, fromDate, toDate);
  return await axios.get(url, config);
};

export const loadAllClients = async (token = null) => {
  let config;

  if (token) {
    // Use the provided token directly
    config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  } else {
    // Use the token from session storage
    config = setAuthHeader();
    if (config === null) throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlForAllClients();
  return await axios.get(url, config);
};

// Server-driven page fetch for the Master Data Client grid. Returns the PageResponse
// envelope (content/page/pageSize/totalElements/totalPages) on response.data.
export const loadClientsPageFromBackend = async ({ page, pageSize, sortBy, sortDirection, searchQuery }) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForClientsPage({ page, pageSize, sortBy, sortDirection, searchQuery });
  return await axios.get(url, config);
};

export const addClientToBackend = async (data) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlToCreateClient();
  return await axios.post(url, data, config);
};

export const updateClientToBackend = async (client_id, data) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlToUpdateClient(client_id);
  return await axios.put(url, data, config);
};

export const deleteClient = async (client_id) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlToDeleteClient(client_id);
  return await axios.delete(url, config);
};

export const loadAllClientMappings = async () => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlForAllClientMappings();
  return await axios.get(url, config);
};

// Server-driven page fetch for the Master Data Client-User Mapping grid. Returns the PageResponse envelope.
export const loadClientMappingsPageFromBackend = async ({ page, pageSize, sortBy, sortDirection, searchQuery }) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForClientMappingsPage({ page, pageSize, sortBy, sortDirection, searchQuery });
  return await axios.get(url, config);
};

export const addClientMappingToBackend = async (data) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlToCreateClientMapping();
  return await axios.post(url, data, config);
};

export const deleteClientMapping = async (deleteObj) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlToDeleteClientMapping(deleteObj);

  return await axios.delete(url, config);
};

export const updateClientMappingToBackend = async (id, data) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlToUpdateClientMapping(id);
  return await axios.put(url, data, config);
};

// ---------------------------------------------------------------------------
// BDM (Business Development Manager) to client mapping — @since NEXT_VERSION (HRMS-116)
// ---------------------------------------------------------------------------

// Returns the clients assigned to a BDM — used to scope the read-only "own teams" view (Phase 3).
export const loadClientsForBdm = async (bdmUserId) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForClientsForBdm(bdmUserId);
  return await axios.get(url, config);
};

/**
 * Alias for loadAllClients - used by ProjectAdminView
 */
export const fetchAllClients = loadAllClients;
